import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Collapse, Typography, Box, Toolbar, alpha,
} from "@mui/material";
import { ExpandMore, VerifiedUserOutlined } from "@mui/icons-material";
import { DRAWER_WIDTH, APPBAR_HEIGHT } from "../../styles/layout";
import { getMenu, gruposConRuta } from "./menuConfig";
import { useModulosUsuario } from "../../hooks/useModulosUsuario";
import { getHomePorRol } from "../utils/rolUtils";

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      pl: "22px", pr: 2, pt: 2.25, pb: 0.5,
      lineHeight: 1.6,
      color: "text.secondary",
      fontSize: "0.65rem",
      opacity: 0.85,
    }}
  >
    {children}
  </Typography>
);

const itemPadding = (indent) => 1.5 + indent * 2;

const iconSx = (active, indent) => ({
  minWidth: 34,
  color: active ? "primary.main" : "text.secondary",
  "& svg": { fontSize: indent > 0 ? 19 : 21 },
});

const NavItem = ({ to, Icon, label, indent = 0, active, onNavigate }) => (
  <ListItem disablePadding sx={{ display: "block" }}>
    <ListItemButton
      component={Link}
      to={to}
      selected={active}
      onClick={onNavigate}
      sx={(t) => ({
        pl: itemPadding(indent),
        pr: 1.25,
        py: 0.7,
        my: 0.25,
        position: "relative",
        "&.Mui-selected": {
          backgroundColor: alpha(t.palette.primary.main, t.palette.mode === "light" ? 0.1 : 0.2),
          "&:hover": {
            backgroundColor: alpha(t.palette.primary.main, t.palette.mode === "light" ? 0.14 : 0.26),
          },
        },
        "&.Mui-selected::before": {
          content: '""',
          position: "absolute",
          left: -8,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: "0 3px 3px 0",
          backgroundColor: "primary.main",
        },
      })}
    >
      <ListItemIcon sx={iconSx(active, indent)}>
        <Icon />
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: "0.84rem",
          fontWeight: active ? 600 : 500,
          color: active ? "primary.main" : "text.primary",
          lineHeight: 1.35,
        }}
      />
    </ListItemButton>
  </ListItem>
);

const GroupHeader = ({ Icon, label, indent = 0, open, active, onToggle }) => (
  <ListItem disablePadding sx={{ display: "block" }}>
    <ListItemButton
      onClick={onToggle}
      aria-expanded={open}
      sx={{ pl: itemPadding(indent), pr: 1.25, py: 0.7, my: 0.25 }}
    >
      <ListItemIcon sx={iconSx(active, indent)}>
        <Icon />
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: "0.84rem",
          fontWeight: 600,
          color: active ? "primary.main" : "text.primary",
          noWrap: true,
        }}
      />
      <ExpandMore
        fontSize="small"
        sx={{
          color: "text.secondary",
          transition: "transform .2s",
          transform: open ? "rotate(180deg)" : "none",
        }}
      />
    </ListItemButton>
  </ListItem>
);

// Contenedor de sub-items con línea guía vertical alineada al ícono del grupo
const GroupChildren = ({ open, indent = 0, children }) => (
  <Collapse in={open} timeout="auto" unmountOnExit>
    <Box
      sx={{
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          left: `${8 + itemPadding(indent) * 8 + 10}px`,
          top: 4,
          bottom: 4,
          width: "1px",
          bgcolor: "divider",
        },
      }}
    >
      {children}
    </Box>
  </Collapse>
);

const Sidebar = ({ mobileOpen, handleDrawerToggle, isOpen, isSmallScreen }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const location = useLocation();
  const { rol, cargando, tieneServicio } = useModulosUsuario();

  // Mientras cargan los módulos, se ocultan los items que dependen de uno
  const menu = useMemo(
    () => getMenu(rol, (servicio) => !cargando && tieneServicio(servicio)),
    [rol, cargando, tieneServicio]
  );

  const gruposActivos = useMemo(() => gruposConRuta(menu, location.pathname), [menu, location.pathname]);

  useEffect(() => {
    if (gruposActivos.length) {
      setExpandedItems((prev) => ({
        ...prev,
        ...Object.fromEntries(gruposActivos.map((id) => [id, true])),
      }));
    }
  }, [gruposActivos]);

  const toggleExpand = (id) =>
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  // En móvil, cerrar el drawer temporal al navegar
  const onNavigate = mobileOpen ? handleDrawerToggle : undefined;

  const renderEntradas = (entradas, indent = 0) =>
    entradas.map((e) => {
      if (e.tipo === "seccion") {
        return <SectionLabel key={`s-${e.label}`}>{e.label}</SectionLabel>;
      }
      if (e.tipo === "grupo") {
        return (
          <React.Fragment key={e.id}>
            <GroupHeader
              Icon={e.icon}
              label={e.label}
              indent={indent}
              open={!!expandedItems[e.id]}
              active={gruposActivos.includes(e.id)}
              onToggle={() => toggleExpand(e.id)}
            />
            <GroupChildren open={!!expandedItems[e.id]} indent={indent}>
              {renderEntradas(e.hijos, indent + 1)}
            </GroupChildren>
          </React.Fragment>
        );
      }
      return (
        <NavItem
          key={e.ruta}
          to={e.ruta}
          Icon={e.icon}
          label={e.label}
          indent={indent}
          active={location.pathname === e.ruta}
          onNavigate={onNavigate}
        />
      );
    });

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", width: DRAWER_WIDTH }}>
      <Toolbar sx={{ minHeight: APPBAR_HEIGHT, flexShrink: 0 }} />

      <Box component="nav" aria-label="Menú principal" sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 1, pb: 2 }}>
        <List disablePadding>{rol && renderEntradas(menu)}</List>
      </Box>

      <Box
        component={Link}
        to={getHomePorRol(rol)}
        onClick={onNavigate}
        sx={(t) => ({
          m: 1.5,
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderRadius: 3,
          textDecoration: "none",
          color: "inherit",
          border: `1px solid ${t.palette.divider}`,
          background: `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.08)}, ${alpha(t.palette.primary.light, 0.02)})`,
          transition: "border-color .15s",
          "&:hover": { borderColor: alpha(t.palette.primary.main, 0.35) },
        })}
      >
        <Box
          sx={{
            width: 34, height: 34, borderRadius: 2, flexShrink: 0,
            display: "grid", placeItems: "center",
            bgcolor: "primary.main", color: "primary.contrastText",
          }}
        >
          <VerifiedUserOutlined sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            Alerta Normativa
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            por Austranet
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: { xs: 0, sm: isOpen && !isSmallScreen ? DRAWER_WIDTH : 0 },
        flexShrink: { sm: 0 },
        transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
            width: isOpen && !isSmallScreen ? DRAWER_WIDTH : 0,
            overflowX: "hidden",
          },
        }}
        open={isOpen && !isSmallScreen}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
